import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Users, Car, Wrench, DollarSign, LogOut, Building2, ShieldCheck, ShieldAlert,
  Activity, FileText, Search, CheckCircle2, XCircle, Plus, Crown, TrendingUp,
  ClipboardList, History, FileCheck,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ClaimsConsole from '../components/ClaimsConsole';

const STATUS_BADGE = {
  approved: { label: 'Aprobado', class: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  pending: { label: 'Pendiente', class: 'bg-amber-100 text-amber-700 border-amber-200' },
  suspended: { label: 'Suspendido', class: 'bg-rose-100 text-rose-700 border-rose-200' },
};

const REQ_STATUS_BADGE = {
  open: { label: 'Abierta', class: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'En progreso', class: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completada', class: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelada', class: 'bg-slate-200 text-slate-700' },
};

const AdminDashboard = () => {
  const { user, API, sessionToken, handleLogout } = useContext(AuthContext);
  const [adminInfo, setAdminInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [talleres, setTalleres] = useState([]);
  const [clients, setClients] = useState([]);
  const [requests, setRequests] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [claimsStats, setClaimsStats] = useState(null);
  const [tallerFilter, setTallerFilter] = useState('all');
  const [requestStatusFilter, setRequestStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Create org dialog
  const [newOrg, setNewOrg] = useState({ name: '', slug: '', type: 'generic', description: '' });
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);

  // Suspend dialog
  const [suspendDialog, setSuspendDialog] = useState({ open: false, taller: null, reason: '' });

  const headers = { Authorization: `Bearer ${sessionToken}` };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [meRes, statsRes, orgsRes, talleresRes, clientsRes, requestsRes, auditRes, claimsStatsRes] = await Promise.all([
        axios.get(`${API}/admin/me`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/organizations`, { headers }),
        axios.get(`${API}/admin/talleres`, { headers }),
        axios.get(`${API}/admin/clients`, { headers }),
        axios.get(`${API}/admin/requests`, { headers }),
        axios.get(`${API}/admin/audit-log`, { headers }),
        axios.get(`${API}/admin/claims/stats`, { headers }),
      ]);
      setAdminInfo(meRes.data);
      setStats(statsRes.data);
      setOrgs(orgsRes.data);
      setTalleres(talleresRes.data);
      setClients(clientsRes.data);
      setRequests(requestsRes.data);
      setAuditLog(auditRes.data);
      setClaimsStats(claimsStatsRes.data);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar el panel de administración');
    } finally {
      setLoading(false);
    }
  };

  const approveTaller = async (tallerId) => {
    try {
      await axios.post(`${API}/admin/talleres/${tallerId}/approve`, {}, { headers });
      toast.success('Taller aprobado');
      fetchAll();
    } catch {
      toast.error('No se pudo aprobar el taller');
    }
  };

  const suspendTaller = async () => {
    if (!suspendDialog.taller) return;
    try {
      await axios.post(
        `${API}/admin/talleres/${suspendDialog.taller.id}/suspend`,
        { reason: suspendDialog.reason },
        { headers }
      );
      toast.success('Taller suspendido');
      setSuspendDialog({ open: false, taller: null, reason: '' });
      fetchAll();
    } catch {
      toast.error('No se pudo suspender el taller');
    }
  };

  const createOrg = async () => {
    if (!newOrg.name) {
      toast.error('Nombre requerido');
      return;
    }
    try {
      await axios.post(`${API}/admin/organizations`, newOrg, { headers });
      toast.success('Organización creada');
      setNewOrg({ name: '', slug: '', type: 'generic', description: '' });
      setOrgDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al crear organización');
    }
  };

  // Filters
  const filteredTalleres = talleres.filter((t) => {
    if (tallerFilter !== 'all' && t.verification_status !== tallerFilter) return false;
    if (searchQuery && !`${t.name} ${t.company_name || ''} ${t.email}`.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const filteredRequests = requests.filter((r) => {
    if (requestStatusFilter !== 'all' && r.status !== requestStatusFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Cargando panel de administración…</div>
      </div>
    );
  }

  const isMaster = adminInfo?.is_master;
  const orgLabel = isMaster ? 'Flix Master' : adminInfo?.org?.name || 'Organización';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center">
              {isMaster ? <Crown className="w-5 h-5 text-white" /> : <Building2 className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                Admin · {orgLabel}
              </h1>
              <p className="text-xs text-slate-500">{adminInfo?.user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Salir
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white border w-full justify-start mb-6 p-1 h-auto flex-wrap" data-testid="admin-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview"><Activity className="w-4 h-4 mr-2" />Resumen</TabsTrigger>
            <TabsTrigger value="talleres" data-testid="tab-talleres"><Wrench className="w-4 h-4 mr-2" />Talleres</TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients"><Users className="w-4 h-4 mr-2" />Clientes</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests"><ClipboardList className="w-4 h-4 mr-2" />Solicitudes</TabsTrigger>
            <TabsTrigger value="claims" data-testid="tab-claims"><FileCheck className="w-4 h-4 mr-2" />Siniestros</TabsTrigger>
            {isMaster && (
              <TabsTrigger value="orgs" data-testid="tab-orgs"><Building2 className="w-4 h-4 mr-2" />Organizaciones</TabsTrigger>
            )}
            <TabsTrigger value="audit" data-testid="tab-audit"><History className="w-4 h-4 mr-2" />Auditoría</TabsTrigger>
          </TabsList>

          {/* RESUMEN */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard testid="stat-clients" icon={<Users className="w-5 h-5" />} label="Clientes" value={stats.total_clients} color="from-blue-500 to-blue-600" />
              <StatCard testid="stat-talleres" icon={<Wrench className="w-5 h-5" />} label="Talleres" value={stats.total_talleres} color="from-purple-500 to-purple-600" sub={`${stats.approved_talleres} aprobados · ${stats.pending_talleres} pendientes`} />
              <StatCard testid="stat-requests" icon={<ClipboardList className="w-5 h-5" />} label="Solicitudes" value={stats.total_requests} color="from-amber-500 to-amber-600" sub={`${stats.open_requests} abiertas · ${stats.in_progress_requests} en curso`} />
              {isMaster ? (
                <StatCard testid="stat-revenue" icon={<DollarSign className="w-5 h-5" />} label="Ingresos (comisión)" value={`$${stats.total_revenue.toLocaleString()}`} color="from-emerald-500 to-emerald-600" sub={`GMV: $${stats.total_gmv.toLocaleString()}`} />
              ) : (
                <StatCard testid="stat-claims" icon={<FileCheck className="w-5 h-5" />} label="Siniestros" value={claimsStats?.total_claims ?? 0} color="from-emerald-500 to-emerald-600" sub={`${claimsStats?.pending_claims ?? 0} pendientes de aprobación`} />
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-900">Tasa de conversión</h3>
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                </div>
                <p className="text-4xl font-bold text-slate-900">{stats.conversion_rate}%</p>
                <p className="text-sm text-slate-500 mt-1">{stats.completed_requests} de {stats.total_requests} solicitudes completadas</p>
              </Card>
              <Card className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-900">Citas agendadas</h3>
                  <Car className="w-5 h-5 text-indigo-500" />
                </div>
                <p className="text-4xl font-bold text-slate-900">{stats.total_appointments}</p>
                <p className="text-sm text-slate-500 mt-1">{stats.total_bids} ofertas emitidas</p>
              </Card>
              {isMaster ? (
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-900">Organizaciones</h3>
                    <Building2 className="w-5 h-5 text-purple-500" />
                  </div>
                  <p className="text-4xl font-bold text-slate-900">{stats.total_orgs}</p>
                  <p className="text-sm text-slate-500 mt-1">Alcance: {stats.scope === 'master' ? 'Flix global' : 'Propia'}</p>
                </Card>
              ) : (
                <Card className="p-6" data-testid="approved-amount-kpi">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-900">Monto aprobado</h3>
                    <ShieldCheck className="w-5 h-5 text-emerald-500" />
                  </div>
                  <p className="text-3xl font-bold text-slate-900">₡{(claimsStats?.total_approved_amount ?? 0).toLocaleString('es-CR')}</p>
                  <p className="text-sm text-slate-500 mt-1">Deducibles: ₡{(claimsStats?.total_deductibles ?? 0).toLocaleString('es-CR')}</p>
                </Card>
              )}
            </div>

            <Card className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Estado de talleres</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <MiniStat label="Aprobados" value={stats.approved_talleres} icon={<ShieldCheck className="w-5 h-5 text-emerald-600" />} bg="bg-emerald-50" />
                <MiniStat label="Pendientes" value={stats.pending_talleres} icon={<ShieldAlert className="w-5 h-5 text-amber-600" />} bg="bg-amber-50" />
                <MiniStat label="Suspendidos" value={stats.suspended_talleres} icon={<XCircle className="w-5 h-5 text-rose-600" />} bg="bg-rose-50" />
              </div>
            </Card>
          </TabsContent>

          {/* TALLERES */}
          <TabsContent value="talleres" className="space-y-4">
            <Card className="p-4">
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
                <div className="flex gap-2 items-center">
                  <Search className="w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre o email"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-72"
                    data-testid="taller-search-input"
                  />
                </div>
                <Select value={tallerFilter} onValueChange={setTallerFilter}>
                  <SelectTrigger className="w-44" data-testid="taller-status-filter">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="approved">Aprobados</SelectItem>
                    <SelectItem value="pending">Pendientes</SelectItem>
                    <SelectItem value="suspended">Suspendidos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Card>

            <Card className="overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-100 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Taller</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Calificación</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredTalleres.map((t) => {
                    const status = t.verification_status || 'approved';
                    const badge = STATUS_BADGE[status];
                    return (
                      <tr key={t.id} data-testid={`taller-row-${t.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{t.company_name || t.name}</div>
                          <div className="text-xs text-slate-500">{t.location?.city || 'Sin ubicación'}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{t.email}</td>
                        <td className="px-4 py-3 text-sm">
                          {t.rating?.toFixed(1) || '0.0'} ★ ({t.total_reviews || 0})
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded border ${badge.class}`}>{badge.label}</span>
                        </td>
                        <td className="px-4 py-3 text-right space-x-2">
                          {status !== 'approved' && (
                            <Button size="sm" onClick={() => approveTaller(t.id)} data-testid={`approve-taller-${t.id}`}>
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                            </Button>
                          )}
                          {status !== 'suspended' && (
                            <Button size="sm" variant="outline" onClick={() => setSuspendDialog({ open: true, taller: t, reason: '' })} data-testid={`suspend-taller-${t.id}`}>
                              <XCircle className="w-4 h-4 mr-1" /> Suspender
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredTalleres.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No hay talleres que coincidan con el filtro.</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          {/* CLIENTS */}
          <TabsContent value="clients" className="space-y-4">
            <Card className="overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-100 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Vehículos</th>
                    <th className="px-4 py-3">Solicitudes</th>
                    <th className="px-4 py-3">Registrado</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {clients.map((c) => (
                    <tr key={c.id} data-testid={`client-row-${c.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{c.email}</td>
                      <td className="px-4 py-3">{c.vehicles_count}</td>
                      <td className="px-4 py-3">{c.requests_count}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {new Date(c.created_at).toLocaleDateString('es-CR')}
                      </td>
                    </tr>
                  ))}
                  {clients.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No hay clientes registrados.</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          {/* REQUESTS */}
          <TabsContent value="requests" className="space-y-4">
            <Card className="p-4">
              <Select value={requestStatusFilter} onValueChange={setRequestStatusFilter}>
                <SelectTrigger className="w-44" data-testid="request-status-filter">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="open">Abiertas</SelectItem>
                  <SelectItem value="in_progress">En progreso</SelectItem>
                  <SelectItem value="completed">Completadas</SelectItem>
                  <SelectItem value="cancelled">Canceladas</SelectItem>
                </SelectContent>
              </Select>
            </Card>

            <Card className="overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-100 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Servicio</th>
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3">Vehículo</th>
                    <th className="px-4 py-3">Ofertas</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredRequests.slice(0, 100).map((r) => {
                    const badge = REQ_STATUS_BADGE[r.status] || { label: r.status, class: 'bg-slate-100 text-slate-700' };
                    return (
                      <tr key={r.id} data-testid={`request-row-${r.id}`}>
                        <td className="px-4 py-3 max-w-xs">
                          <div className="font-medium text-slate-900 truncate">{r.service_type}</div>
                          <div className="text-xs text-slate-500 truncate">{r.description}</div>
                        </td>
                        <td className="px-4 py-3 text-sm">{r.client_info?.name || '—'}</td>
                        <td className="px-4 py-3 text-sm">
                          {r.vehicle_info ? `${r.vehicle_info.make} ${r.vehicle_info.model}` : '—'}
                        </td>
                        <td className="px-4 py-3">{r.bids_count}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded ${badge.class}`}>{badge.label}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {new Date(r.created_at).toLocaleDateString('es-CR')}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredRequests.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Sin solicitudes.</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          {/* CLAIMS (Siniestros) */}
          <TabsContent value="claims" className="space-y-4">
            <ClaimsConsole onChanged={fetchAll} />
          </TabsContent>

          {/* ORGANIZATIONS (master only) */}
          {isMaster && (
            <TabsContent value="orgs" className="space-y-4">
              <div className="flex justify-end">
                <Dialog open={orgDialogOpen} onOpenChange={setOrgDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="new-org-btn"><Plus className="w-4 h-4 mr-2" />Nueva organización</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Crear organización</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                      <Input
                        placeholder="Nombre"
                        value={newOrg.name}
                        onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                        data-testid="new-org-name"
                      />
                      <Input
                        placeholder="Slug (opcional, ej: ins-demo)"
                        value={newOrg.slug}
                        onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value })}
                        data-testid="new-org-slug"
                      />
                      <Select value={newOrg.type} onValueChange={(v) => setNewOrg({ ...newOrg, type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="generic">Genérica</SelectItem>
                          <SelectItem value="insurance">Aseguradora</SelectItem>
                          <SelectItem value="dealer">Concesionaria</SelectItem>
                          <SelectItem value="fleet">Flotilla</SelectItem>
                        </SelectContent>
                      </Select>
                      <Textarea
                        placeholder="Descripción"
                        value={newOrg.description}
                        onChange={(e) => setNewOrg({ ...newOrg, description: e.target.value })}
                        data-testid="new-org-description"
                      />
                    </div>
                    <DialogFooter>
                      <Button onClick={createOrg} data-testid="new-org-submit">Crear</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {orgs.map((o) => (
                  <Card key={o.id} className="p-5" data-testid={`org-card-${o.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">{o.name}</h3>
                          {o.type === 'master' && <Crown className="w-4 h-4 text-amber-500" />}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{o.slug}</p>
                      </div>
                      <Badge variant="outline">{o.type}</Badge>
                    </div>
                    {o.description && <p className="text-sm text-slate-600 mt-3">{o.description}</p>}
                  </Card>
                ))}
              </div>
            </TabsContent>
          )}

          {/* AUDIT LOG */}
          <TabsContent value="audit" className="space-y-4">
            <Card className="overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-100 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Fecha</th>
                    <th className="px-4 py-3">Actor</th>
                    <th className="px-4 py-3">Acción</th>
                    <th className="px-4 py-3">Objetivo</th>
                    <th className="px-4 py-3">Detalle</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {auditLog.map((l) => (
                    <tr key={l.id} data-testid={`audit-row-${l.id}`}>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                        {new Date(l.created_at).toLocaleString('es-CR')}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium">{l.actor_email}</div>
                        <div className="text-xs text-slate-500">{l.actor_role}</div>
                      </td>
                      <td className="px-4 py-3"><Badge variant="outline">{l.action}</Badge></td>
                      <td className="px-4 py-3 text-xs">{l.target_type}/{l.target_id?.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{l.details && JSON.stringify(l.details)}</td>
                    </tr>
                  ))}
                  {auditLog.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Sin actividad registrada todavía.</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Suspend dialog */}
      <Dialog open={suspendDialog.open} onOpenChange={(open) => setSuspendDialog({ ...suspendDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspender taller</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Estás por suspender <strong>{suspendDialog.taller?.company_name || suspendDialog.taller?.name}</strong>.
          </p>
          <Textarea
            placeholder="Motivo de suspensión (opcional)"
            value={suspendDialog.reason}
            onChange={(e) => setSuspendDialog({ ...suspendDialog, reason: e.target.value })}
            data-testid="suspend-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDialog({ open: false, taller: null, reason: '' })}>Cancelar</Button>
            <Button variant="destructive" onClick={suspendTaller} data-testid="confirm-suspend-btn">Confirmar suspensión</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ icon, label, value, color, sub, testid }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm text-slate-500">{label}</span>
      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${color} text-white flex items-center justify-center`}>
        {icon}
      </div>
    </div>
    <div className="text-3xl font-bold text-slate-900">{value}</div>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </Card>
);

const MiniStat = ({ label, value, icon, bg }) => (
  <div className={`${bg} rounded-lg p-4 flex items-center justify-between`}>
    <div>
      <p className="text-sm text-slate-600">{label}</p>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
    </div>
    {icon}
  </div>
);

export default AdminDashboard;
